import { createEffect, type JSX, ErrorBoundary as SolidErrorBoundary, splitProps } from "solid-js";
import { msg } from "@lingui/core/macro";
import { RefreshCw, RotateCw } from "lucide-solid";

import { GlassSurface } from "@/components/ui/glass-surface";
import { Button } from "@/components/ui/button";
import { ErrorCookingIllustration } from "@/components/ui/illustrations";
import { useI18n } from "@/providers/i18n-provider";
import { cn } from "@mealplan/shared";

interface AppErrorBoundaryProps {
  children: JSX.Element;
  /**
   * "page": fallback ocupa toda la altura disponible (root / regiones grandes).
   * "region": fallback compacto, embebido dentro de una sección.
   */
  variant?: "page" | "region";
  /** Etiqueta de la región para el log (p. ej. "planner", "recipes"). */
  region?: string;
  class?: string;
}

interface FallbackProps {
  error: unknown;
  reset: () => void;
  variant: "page" | "region";
  region?: string;
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === "string" && error) {
    return error;
  }
  return fallback;
}

function ErrorFallback(props: FallbackProps) {
  const { t } = useI18n();

  // Loggea SIEMPRE el error capturado (telemetría mínima en consola).
  createEffect(() => {
    console.error(
      `[ErrorBoundary${props.region ? `:${props.region}` : ""}] Caught render error:`,
      props.error,
    );
  });

  const isPage = () => props.variant === "page";

  return (
    <div
      role="alert"
      aria-live="assertive"
      class={cn(
        "flex w-full items-center justify-center p-4",
        isPage() ? "min-h-[60dvh] flex-1" : "min-h-[200px]",
      )}
    >
      <GlassSurface
        class={cn(
          "flex w-full flex-col items-center text-center",
          isPage() ? "max-w-md p-8 space-y-6" : "max-w-sm p-6 space-y-4",
        )}
      >
        <ErrorCookingIllustration class={cn("text-accent", isPage() ? "h-28 w-36" : "h-20 w-28")} />
        <div class="space-y-2">
          <h2 class={cn("font-serif font-bold tracking-tight", isPage() ? "text-2xl" : "text-lg")}>
            {t(msg`Something burned in the kitchen`)}
          </h2>
          <p class="text-sm text-muted-foreground">
            {t(
              msg`We hit an unexpected error rendering this view. Your data is safe — try again or reload.`,
            )}
          </p>
          <p class="text-xs text-muted-foreground/70 break-words">
            {errorMessage(props.error, t(msg`An unexpected error has occurred.`))}
          </p>
        </div>
        <div class="flex w-full flex-col gap-2 sm:flex-row">
          <Button
            class="flex-1 gap-2"
            onClick={() => {
              props.reset();
            }}
            data-testid="error-boundary-retry"
          >
            <RotateCw class="h-4 w-4" />
            {t(msg`Retry`)}
          </Button>
          <Button
            variant="outline"
            class="flex-1 gap-2"
            onClick={() => {
              globalThis.location.reload();
            }}
          >
            <RefreshCw class="h-4 w-4" />
            {t(msg`Reload`)}
          </Button>
        </div>
      </GlassSurface>
    </div>
  );
}

/**
 * ErrorBoundary global y de región para MealPlan (Fase 5, eje 1).
 *
 * Envuelve `<ErrorBoundary>` de Solid con un fallback PREMIUM theme-aware:
 * ilustración SVG culinaria, título, descripción, "Reintentar" (resetea el
 * boundary y vuelve a renderizar) y "Recargar". Loggea siempre el error.
 * Nunca deja una pantalla en blanco.
 */
export function AppErrorBoundary(props: AppErrorBoundaryProps) {
  const [local] = splitProps(props, ["children", "variant", "region", "class"]);
  // The `fallback` render prop is idiomatic for Solid's ErrorBoundary; it is not
  // an unstable nested component (it only forwards into the top-level
  // <ErrorFallback>), so the lint heuristic is a false positive here.
  // oxlint-disable-next-line react/no-unstable-nested-components
  const renderFallback = (error: unknown, reset: () => void): JSX.Element => (
    <ErrorFallback
      error={error}
      reset={reset}
      variant={local.variant ?? "page"}
      region={local.region}
    />
  );
  return <SolidErrorBoundary fallback={renderFallback}>{local.children}</SolidErrorBoundary>;
}
